import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditListDialog } from '@/components/messaging/edit-list-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

interface ListRow {
  id: string;
  name: string;
  kind: 'fixed' | 'dynamic';
  memberCount: number;
  description: string | null;
}

export const MessageListsPanel = () => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const [editId, setEditId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const list = useQuery({
    queryKey: ['messaging', 'lists', page, pageSize],
    queryFn: () =>
      api.get<{
        data: ListRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/messaging/lists?page=${page}&pageSize=${pageSize}`),
  });

  const columns: DataTableColumn<ListRow>[] = [
    {
      id: 'name',
      header: t('lists.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'kind',
      header: t('lists.columns.kind'),
      cell: (row) =>
        row.kind === 'fixed' ? t('lists.kindFixed') : t('lists.kindDynamic'),
    },
    {
      id: 'members',
      header: t('lists.columns.members'),
      cell: (row) => t('lists.memberCount', { count: row.memberCount }),
    },
  ];

  const pageMeta = list.data?.page;

  return (
    <>
      <DataTable
        rows={list.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('lists.empty')}</p>}
        actionsHeader={tCommon('table.actions')}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditId(row.id);
              setEditOpen(true);
            }}
          >
            {t('lists.open')}
          </Button>
        )}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          className="mt-4"
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <EditListDialog
        open={editOpen}
        onOpenChange={(next) => {
          setEditOpen(next);
          if (!next) setEditId(null);
        }}
        listId={editId}
      />
    </>
  );
};
